import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  ContentArea,
  PageTitle,
  TitleArea,
} from "../Announcements/styles";
import Header from "../../components/Header";
import IconsBar from "../../components/IconsBar/IconsBar";
import { AuthContext } from "../../contexts/Auth/AuthContext";
import { useContext } from "react";
import {
  listPlans,
  updatePlan,
  toggleCheck,
  toggleEditEnabled,
} from "../../api/endpoints/plans";
import { getErrorMessage } from "../../utils/errorHandler";
import { AlertMessages } from "../../components/AlertMessages";
import NewPlanForm from "../../components/Planning/NewPlanForm";
import { getTeachers } from "../../api/autenticoAPI";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Chip,
  Pagination,
  Stack,
  Grid,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";

// Componente de planejamento escolar
const PlanningDashboard = () => {
  const { user } = useContext(AuthContext);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewPlanForm, setShowNewPlanForm] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [typeMessage, setTypeMessage] = useState("success");
  const [contentMessage, setContentMessage] = useState("");
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 10,
    offset: 0,
    totalPages: 1,
    currentPage: 1,
  });
  const [filters, setFilters] = useState({
    teacherId: "",
    subjectId: "",
    gradeId: "",
    planType: "",
    checkStatus: "",
    startDate: "",
    endDate: "",
  });
  const [teachers, setTeachers] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [editForm, setEditForm] = useState({
    planType: "DAILY",
    date: "",
    weekStart: "",
    weekEnd: "",
    unit: "",
    classActivities: "",
    homework: "",
    resources: "",
  });

  const toInputDate = (isoString) => {
    if (!isoString) return "";
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  };

  const openEditModal = (plan) => {
    setEditingPlan(plan);
    setEditForm({
      planType: (plan.planType || "DAILY").toUpperCase(),
      date: toInputDate(plan.date),
      weekStart: toInputDate(plan.weekStart),
      weekEnd: toInputDate(plan.weekEnd),
      unit: plan.unit || "",
      classActivities: plan.classActivities || "",
      homework: plan.homework || "",
      resources: plan.resources || "",
    });
    setEditModalOpen(true);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "planType"
        ? { weekStart: "", weekEnd: "", date: "" }
        : {}),
    }));
  };

  const handleSaveEditedPlan = async () => {
    if (!editingPlan) return;
    try {
      setLoading(true);
      const payload = {
        planType: editForm.planType,
        date: editForm.planType === "DAILY" ? editForm.date : null,
        weekStart: editForm.planType === "WEEKLY" ? editForm.weekStart : null,
        weekEnd: editForm.planType === "WEEKLY" ? editForm.weekEnd : null,
        unit: editForm.unit,
        classActivities: editForm.classActivities,
        homework: editForm.homework,
        resources: editForm.resources,
      };
      await updatePlan(editingPlan.id, payload);
      setTypeMessage("success");
      setContentMessage("Planejamento atualizado com sucesso!");
      setShowAlert(true);
      setEditModalOpen(false);
      setEditingPlan(null);
      await loadPlans(pagination.currentPage);
    } catch (error) {
      const errorMsg = getErrorMessage(error);
      setTypeMessage("error");
      setContentMessage(errorMsg);
      setShowAlert(true);
    } finally {
      setLoading(false);
    }
  };

  // Carregar planejamentos
  const loadPlans = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        const result = await listPlans(filters, page, pagination.limit);
        
        // Verificar se retornou com paginação
        if (result && result.data && result.pagination) {
          setPlans(result.data);
          setPagination(result.pagination);
        } else if (Array.isArray(result)) {
          // Compatibilidade com formato antigo (sem paginação)
          setPlans(result);
          setPagination({
            total: result.length,
            limit: result.length,
            offset: 0,
            totalPages: 1,
            currentPage: 1,
          });
        } else {
          setPlans([]);
        }
      } catch (error) {
        const errorMsg = getErrorMessage(error);
        setTypeMessage("error");
        setContentMessage(errorMsg);
        setShowAlert(true);
      } finally {
        setLoading(false);
      }
    },
    [filters, pagination.limit]
  );

  // Carregar lista de professores (para coordenadores/admins)
  useEffect(() => {
    const loadTeachers = async () => {
      if (user && (user.level === "coordinator" || user.level === "admin")) {
        try {
          const teachersList = await getTeachers();
          setTeachers(teachersList || []);
        } catch (error) {
          console.error("Erro ao carregar professores:", error);
        }
      }
    };
    loadTeachers();
  }, [user]);

  useEffect(() => {
    loadPlans(1);
  }, [filters]);

  const handlePageChange = (event, value) => {
    loadPlans(value);
  };

  // handleUpdatePlan removido - não utilizado diretamente (usado via NewPlanForm)

  const handleToggleCheck = async (planId) => {
    try {
      await toggleCheck(planId);
      setTypeMessage("success");
      setContentMessage("Status atualizado com sucesso!");
      setShowAlert(true);
      loadPlans(pagination.currentPage);
    } catch (error) {
      const errorMsg = getErrorMessage(error);
      setTypeMessage("error");
      setContentMessage(errorMsg);
      setShowAlert(true);
    }
  };

  const handleToggleEditEnabled = async (planId) => {
    try {
      await toggleEditEnabled(planId);
      setTypeMessage("success");
      setContentMessage("Permissão de edição atualizada!");
      setShowAlert(true);
      loadPlans(pagination.currentPage);
    } catch (error) {
      const errorMsg = getErrorMessage(error);
      setTypeMessage("error");
      setContentMessage(errorMsg);
      setShowAlert(true);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "APPROVED":
        return "success";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "APPROVED":
        return "Aprovado";
      default:
        return "Pendente";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("pt-BR");
  };

  return (
    <Container>
      <Header />
      <IconsBar />
      <AlertMessages
        show={showAlert}
        setShow={setShowAlert}
        typeMessage={typeMessage}
        contentMessage={contentMessage}
      />
      <ContentArea>
        <TitleArea>
          <PageTitle>Planejamento Escolar</PageTitle>
          {user && user.level === "teacher" && (
            <Button
              variant="contained"
              onClick={() => setShowNewPlanForm(true)}
              sx={{
                backgroundColor: "#1e958c",
                "&:hover": {
                  backgroundColor: "#167d75",
                },
                borderRadius: "8px",
                padding: "10px 20px",
                fontWeight: 600,
              }}
            >
              Novo Planejamento
            </Button>
          )}
        </TitleArea>

        {/* Filtros - Layout Moderno */}
        <Paper
          elevation={2}
          sx={{
            padding: 3,
            marginBottom: 3,
            borderRadius: 2,
            backgroundColor: "#ffffff",
          }}
        >
          <Typography
            variant="h6"
            sx={{ marginBottom: 2.5, color: "#1e958c", fontWeight: 600 }}
          >
            Filtros
          </Typography>
          <Grid container spacing={2} alignItems="flex-end">
            {/* Primeira linha: Professor, Tipo de Planejamento, Status */}
            {(user?.level === "coordinator" || user?.level === "admin") && (
              <Grid item xs={12} sm={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Professor</InputLabel>
                  <Select
                    value={filters.teacherId}
                    onChange={(e) =>
                      setFilters({ ...filters, teacherId: e.target.value })
                    }
                    label="Professor"
                  >
                    <MenuItem value="">Todos os professores</MenuItem>
                    {teachers.map((teacher) => (
                      <MenuItem key={teacher.id} value={teacher.userId || teacher.id}>
                        {teacher.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={(user?.level === "coordinator" || user?.level === "admin") ? 4 : 6}>
              <FormControl fullWidth>
                <InputLabel>Tipo de Planejamento</InputLabel>
                <Select
                  value={filters.planType}
                  onChange={(e) =>
                    setFilters({ ...filters, planType: e.target.value })
                  }
                  label="Tipo de Planejamento"
                >
                  <MenuItem value="">Todos os tipos</MenuItem>
                  <MenuItem value="WEEKLY">Semanal</MenuItem>
                  <MenuItem value="DAILY">Diário</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={(user?.level === "coordinator" || user?.level === "admin") ? 4 : 6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.checkStatus}
                  onChange={(e) =>
                    setFilters({ ...filters, checkStatus: e.target.value })
                  }
                  label="Status"
                >
                  <MenuItem value="">Todos os status</MenuItem>
                  <MenuItem value="PENDING">Pendente</MenuItem>
                  <MenuItem value="APPROVED">Aprovado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Segunda linha: Data Início, Data Fim, Limpar Filtros */}
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Data Início"
                type="date"
                value={filters.startDate}
                onChange={(e) =>
                  setFilters({ ...filters, startDate: e.target.value })
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                label="Data Fim"
                type="date"
                value={filters.endDate}
                onChange={(e) =>
                  setFilters({ ...filters, endDate: e.target.value })
                }
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => {
                  setFilters({
                    teacherId: "",
                    planType: "",
                    checkStatus: "",
                    startDate: "",
                    endDate: "",
                  });
                  loadPlans(1);
                }}
                sx={{
                  height: "56px",
                  borderColor: "#1e958c",
                  color: "#1e958c",
                  "&:hover": {
                    borderColor: "#1e958c",
                    backgroundColor: "rgba(30, 149, 140, 0.04)",
                  },
                }}
              >
                Limpar Filtros
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Lista de planejamentos */}
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "400px",
            }}
          >
            <CircularProgress />
          </Box>
        ) : plans.length === 0 ? (
          <Paper
            sx={{
              padding: 4,
              textAlign: "center",
              backgroundColor: "#f8f9fa",
            }}
          >
            <Typography variant="h6" color="text.secondary">
              Nenhum planejamento encontrado
            </Typography>
          </Paper>
        ) : (
          <>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {plans.map((plan) => (
                <Card key={plan.id} elevation={2} sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, marginBottom: 0.5 }}
                        >
                          {plan.subject} - {plan.grade}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {(plan.planType?.toUpperCase() || plan.planType) === "WEEKLY" ? "Semanal" : "Diário"}
                          {plan.date && ` - ${formatDate(plan.date)}`}
                          {plan.weekStart &&
                            ` - ${formatDate(plan.weekStart)} a ${formatDate(
                              plan.weekEnd
                            )}`}
                        </Typography>
                        {/* Mostrar nome do professor para coordenadores/admins */}
                        {(user?.level === "coordinator" || user?.level === "admin") && plan.teacherName && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontStyle: "italic" }}>
                            Professor: {plan.teacherName}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <Chip
                          label={getStatusLabel(plan.checkStatus)}
                          color={getStatusColor(plan.checkStatus)}
                          size="small"
                        />
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => {
                            setSelectedPlan(plan);
                            setDetailsModalOpen(true);
                          }}
                          sx={{ ml: 1 }}
                          title="Ver detalhes completos"
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        justifyContent: "flex-end",
                        mt: 2,
                      }}
                    >
                      {(user?.level === "coordinator" ||
                        user?.level === "admin") && (
                        <>
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            onClick={() => handleToggleCheck(plan.id)}
                          >
                            {plan.coordinatorChecked ? "Desaprovar" : "Aprovar"}
                          </Button>
                          {plan.coordinatorChecked && (
                            <Button
                              variant="contained"
                              color={plan.isEditEnabled ? "error" : "warning"}
                              size="small"
                              onClick={() => handleToggleEditEnabled(plan.id)}
                            >
                              {plan.isEditEnabled
                                ? "Bloquear edição"
                                : "Liberar edição"}
                            </Button>
                          )}
                        </>
                      )}
                      {user?.level === "teacher" &&
                        (plan.checkStatus === "PENDING" ||
                          (plan.checkStatus === "APPROVED" && plan.isEditEnabled)) && (
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() => openEditModal(plan)}
                        >
                          Editar
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>

            {/* Paginação */}
            {pagination.totalPages > 1 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  marginTop: 4,
                  padding: 2,
                }}
              >
                <Stack spacing={2}>
                  <Pagination
                    count={pagination.totalPages}
                    page={pagination.currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                  />
                  <Typography variant="body2" color="text.secondary" align="center">
                    Mostrando {pagination.offset + 1} -{" "}
                    {Math.min(
                      pagination.offset + pagination.limit,
                      pagination.total
                    )}{" "}
                    de {pagination.total} planejamentos
                  </Typography>
                </Stack>
              </Box>
            )}
          </>
        )}

        {/* Modal de novo planejamento */}
        {showNewPlanForm && (
          <NewPlanForm
            onClose={() => setShowNewPlanForm(false)}
            onSuccess={() => {
              setTypeMessage("success");
              setContentMessage("Planejamento criado com sucesso!");
              setShowAlert(true);
              loadPlans(pagination.currentPage);
            }}
            user={user}
          />
        )}

        {/* Modal de Detalhes do Planejamento */}
        <Dialog
          open={detailsModalOpen}
          onClose={() => setDetailsModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6">
                {selectedPlan?.subject} - {selectedPlan?.grade}
              </Typography>
              <IconButton onClick={() => setDetailsModalOpen(false)} size="small">
                <Typography>✕</Typography>
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {selectedPlan && (
              <Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Tipo:</strong> {(selectedPlan.planType?.toUpperCase() || selectedPlan.planType) === "WEEKLY" ? "Semanal" : "Diário"}
                  </Typography>
                  {selectedPlan.date && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Data:</strong> {formatDate(selectedPlan.date)}
                    </Typography>
                  )}
                  {selectedPlan.weekStart && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Período:</strong> {formatDate(selectedPlan.weekStart)} a {formatDate(selectedPlan.weekEnd)}
                    </Typography>
                  )}
                  {selectedPlan.teacherName && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Professor:</strong> {selectedPlan.teacherName}
                    </Typography>
                  )}
                  <Chip
                    label={getStatusLabel(selectedPlan.checkStatus)}
                    color={getStatusColor(selectedPlan.checkStatus)}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Conteúdo
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", textAlign: "justify" }}>
                    {selectedPlan.unit}
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Atividades em Sala
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", textAlign: "justify" }}>
                    {selectedPlan.classActivities}
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Atividades para Casa
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", textAlign: "justify" }}>
                    {selectedPlan.homework}
                  </Typography>
                </Box>
                {selectedPlan.resources && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Recursos Utilizados
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", textAlign: "justify" }}>
                        {selectedPlan.resources}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsModalOpen(false)}>Fechar</Button>
          </DialogActions>
        </Dialog>

        {/* Modal de Edição (Professor) */}
        <Dialog
          open={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6">
                Editar Planejamento
              </Typography>
              <IconButton onClick={() => setEditModalOpen(false)} size="small">
                <Typography>✕</Typography>
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                {editingPlan?.subject} - {editingPlan?.grade}
              </Typography>

              {/* Tipo de planejamento e data/semana */}
              {editForm.planType === "WEEKLY" ? (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo de Planejamento</InputLabel>
                    <Select
                      label="Tipo de Planejamento"
                      value={editForm.planType}
                      onChange={(e) => handleEditFormChange("planType", e.target.value)}
                    >
                      <MenuItem value="DAILY">Diário</MenuItem>
                      <MenuItem value="WEEKLY">Semanal</MenuItem>
                    </Select>
                  </FormControl>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                    <TextField
                      label="Início da Semana"
                      type="date"
                      value={editForm.weekStart}
                      onChange={(e) => handleEditFormChange("weekStart", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="Fim da Semana"
                      type="date"
                      value={editForm.weekEnd}
                      onChange={(e) => handleEditFormChange("weekEnd", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Tipo de Planejamento</InputLabel>
                    <Select
                      label="Tipo de Planejamento"
                      value={editForm.planType}
                      onChange={(e) => handleEditFormChange("planType", e.target.value)}
                    >
                      <MenuItem value="DAILY">Diário</MenuItem>
                      <MenuItem value="WEEKLY">Semanal</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    label="Data"
                    type="date"
                    value={editForm.date}
                    onChange={(e) => handleEditFormChange("date", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Box>
              )}

              <TextField
                label="Conteúdo/Unidade"
                multiline
                minRows={4}
                value={editForm.unit}
                onChange={(e) => handleEditFormChange("unit", e.target.value)}
              />
              <TextField
                label="Atividades em Sala"
                multiline
                minRows={4}
                value={editForm.classActivities}
                onChange={(e) => handleEditFormChange("classActivities", e.target.value)}
              />
              <TextField
                label="Atividades para Casa"
                multiline
                minRows={4}
                value={editForm.homework}
                onChange={(e) => handleEditFormChange("homework", e.target.value)}
              />
              <TextField
                label="Recursos Utilizados (opcional)"
                multiline
                minRows={3}
                value={editForm.resources}
                onChange={(e) => handleEditFormChange("resources", e.target.value)}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditModalOpen(false)}>Cancelar</Button>
            <Button
              onClick={handleSaveEditedPlan}
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? "Salvando..." : "Salvar"}
            </Button>
          </DialogActions>
        </Dialog>
      </ContentArea>
    </Container>
  );
};

export default PlanningDashboard;
